<template>
    <section class="student-enrolled">
        <div class="container student-enrolled__container">
            <StudentSidebar :student="student" />

            <main class="student-enrolled__main">
                <header class="student-enrolled__topbar">
                    <div>
                        <span class="student-enrolled__eyebrow">My learning</span>
                        <h1>Enrolled Courses</h1>
                        <p>Browse all courses you are currently enrolled in and continue your learning progress.</p>
                    </div>

                    <a href="/student/dashboard" class="student-enrolled__back">
                        <i class="ri-arrow-left-line"></i>
                        Dashboard
                    </a>
                </header>

                <section class="student-enrolled__search-panel">
                    <div>
                        <span class="student-enrolled__eyebrow">Search courses</span>
                        <h2>Find Your Course</h2>
                    </div>

                    <label class="student-enrolled__search-box">
                        <i class="ri-search-line"></i>

                        <input
                            v-model="search"
                            type="text"
                            placeholder="Search by course name..."
                        />

                        <button v-if="search" type="button" class="student-enrolled__clear" @click="clearSearch">
                            <i class="ri-close-line"></i>
                        </button>
                    </label>
                </section>

                <section class="student-enrolled__panel">
                    <div class="student-enrolled__panel-head">
                        <div>
                            <span class="student-enrolled__eyebrow">All courses</span>
                            <h2>My Enrolled Courses</h2>
                        </div>
                    </div>

                    <div class="student-enrolled__table-wrap">
                        <table class="student-enrolled__table">
                            <thead>
                                <tr>
                                    <th>Course Name</th>
                                    <th>Progress</th>
                                    <th>Status</th>
                                    <th class="text-end">Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr v-for="course in courses" :key="course.id ?? course.title">
                                    <td>
                                        <div class="student-enrolled__course-title">
                                            <strong>{{ course.title }}</strong>
                                        </div>
                                    </td>

                                    <td>
                                        <div class="student-enrolled__progress-cell">
                                            <div class="student-enrolled__progress">
                                                <span :style="{ width: `${course.progress ?? 0}%` }"></span>
                                            </div>
                                            <small>{{ course.progress ?? 0 }}%</small>
                                        </div>
                                    </td>

                                    <td>
                                        <span
                                            class="student-enrolled__status"
                                            :class="`is-${course.statusTone || 'progress'}`"
                                        >
                                            {{ course.status || 'Active' }}
                                        </span>
                                    </td>

                                    <td class="text-end">
                                        <button
                                            type="button"
                                            class="student-enrolled__continue"
                                            :class="{ 'is-disabled': course.isExpired }"
                                            @click="handleCourseAction(course)"
                                        >
                                            {{ course.actionLabel || 'Continue' }}
                                            <i class="ri-arrow-right-line"></i>
                                        </button>
                                    </td>
                                </tr>

                                <tr v-if="!courses.length">
                                    <td colspan="4">
                                        <div class="student-enrolled__empty">
                                            <i class="ri-book-open-line"></i>
                                            <strong>No enrolled courses found.</strong>
                                            <span>Your enrolled courses will appear here after subscription.</span>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="student-enrolled__footer">
                        <p>
                            Showing {{ pagination.from ?? 0 }} to {{ pagination.to ?? 0 }}
                            of {{ pagination.total }} courses
                        </p>

                        <nav v-if="pagination.last_page > 1" class="student-enrolled__pagination" aria-label="Course pagination">
                            <a
                                v-for="link in pagination.links"
                                :key="`${link.label}-${link.url}`"
                                :href="link.url || '#'"
                                :class="{
                                    'is-active': link.active,
                                    'is-disabled': !link.url,
                                }"
                                @click="handlePaginationClick($event, link)"
                                v-html="link.label"
                            ></a>
                        </nav>
                    </div>
                </section>
            </main>
        </div>
    </section>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import StudentSidebar from '@/components/website/student/StudentSidebar.vue';

const root = document.getElementById('studentEnrolledCoursesComponent');

const parseJson = (value, fallback = {}) => {
    try {
        return value ? JSON.parse(value) : fallback;
    } catch (error) {
        return fallback;
    }
};

const student = parseJson(root?.dataset.student);

const initialPagination = parseJson(root?.dataset.courses, {
    data: [],
    current_page: 1,
    last_page: 1,
    per_page: 25,
    total: 0,
    from: 0,
    to: 0,
    links: [],
});

const searchUrl = root?.dataset.searchUrl || '/student/enrolled-courses/list';
const pagination = ref(initialPagination);
const search = ref('');
let searchTimer = null;

const courses = computed(() => pagination.value.data ?? []);

const updateBrowserUrl = (page = 1) => {
    const params = new URLSearchParams();

    if (Number(page) > 1) {
        params.set('page', page);
    }

    const queryString = params.toString();
    const url = queryString ? `/student/enrolled-courses?${queryString}` : '/student/enrolled-courses';

    window.history.replaceState({}, '', url);
};

updateBrowserUrl(initialPagination.current_page || 1);

const fetchCourses = async (page = 1) => {
    const params = {};

    if (search.value.trim()) {
        params.search = search.value.trim();
    }

    if (Number(page) > 1) {
        params.page = page;
    }

    const { data } = await window.axios.get(searchUrl, { params });

    if (data?.courses) {
        pagination.value = data.courses;
        updateBrowserUrl(data.courses.current_page || page);
    }
};

const queueSearch = () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
        fetchCourses(1);
    }, 350);
};

const clearSearch = () => {
    search.value = '';
};

const handlePaginationClick = (event, link) => {
    event.preventDefault();

    if (!link.url || link.active) {
        return;
    }

    const url = new URL(link.url, window.location.origin);
    fetchCourses(url.searchParams.get('page') || 1);
};

const handleCourseAction = (course) => {
    if (!course?.isExpired && course?.detailUrl) {
        window.location.href = course.detailUrl;
        return;
    }

    if (course?.isExpired && window.Swal) {
        window.Swal.fire({
            icon: 'warning',
            title: 'Course Expired',
            text: course.actionMessage || 'Your course access period has ended. Please enroll again to continue learning.',
            confirmButtonText: 'Re-enroll Now',
            confirmButtonColor: '#0d6efd',
        }).then((result) => {
            if (result.isConfirmed) {
                window.location.href = course.purchaseUrl || '/online-training';
            }
        });

        return;
    }

    if (course?.isExpired) {
        window.alert(course.actionMessage || 'Your course access period has ended. Please enroll again to continue learning.');
    }
};

watch(search, queueSearch);

onBeforeUnmount(() => {
    clearTimeout(searchTimer);
});
</script>

<style scoped>
.student-enrolled {
    background:
        radial-gradient(circle at top left, rgba(13, 110, 253, 0.1), transparent 32%),
        linear-gradient(180deg, #f5f8fc 0%, #edf1f6 52%, #f8fafc 100%);
    padding: 32px 0 46px;
}

.student-enrolled__container {
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr);
    gap: 24px;
    width: 100%;
    align-items: start;
}

.student-enrolled__main {
    min-width: 0;
}

.student-enrolled__topbar,
.student-enrolled__search-panel,
.student-enrolled__panel {
    background: #ffffff;
    border: 1px solid rgba(226, 232, 240, 0.92);
    border-radius: 8px;
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.06);
}

.student-enrolled__topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 18px 22px;
    margin-bottom: 18px;
    background:
        linear-gradient(135deg, rgba(13, 110, 253, 0.08), rgba(32, 201, 151, 0.06)),
        #ffffff;
    border-color: #dcecff;
}

.student-enrolled__topbar h1,
.student-enrolled__panel-head h2,
.student-enrolled__search-panel h2 {
    margin: 0;
    color: #111827;
    font-weight: 800;
}

.student-enrolled__topbar h1 {
    font-size: 24px;
}

.student-enrolled__topbar p {
    max-width: 560px;
    margin: 6px 0 0;
    color: #64748b;
    font-size: 13px;
    line-height: 1.5;
}

.student-enrolled__eyebrow {
    display: block;
    margin-bottom: 4px;
    color: #0d6efd;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0;
    text-transform: uppercase;
}

.student-enrolled__back {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 40px;
    padding: 9px 14px;
    border-radius: 8px;
    background: #e7f1ff;
    color: #0d6efd;
    font-size: 13px;
    font-weight: 800;
    text-decoration: none;
    transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.student-enrolled__back:hover {
    background: #dbeafe;
    color: #0b5ed7;
    transform: translateY(-1px);
}

.student-enrolled__search-panel {
    display: grid;
    grid-template-columns: minmax(180px, 240px) minmax(0, 1fr);
    gap: 18px;
    align-items: center;
    padding: 18px 22px;
    margin-bottom: 18px;
}

.student-enrolled__search-panel h2 {
    font-size: 19px;
}

.student-enrolled__search-box {
    display: grid;
    grid-template-columns: 22px minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px;
    min-height: 48px;
    padding: 0 10px 0 14px;
    border: 1px solid #dbe4ef;
    border-radius: 8px;
    background: #f8fafc;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.student-enrolled__search-box:focus-within {
    border-color: #0d6efd;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.12);
}

.student-enrolled__search-box > i {
    color: #64748b;
    font-size: 18px;
}

.student-enrolled__search-box input {
    width: 100%;
    border: 0;
    outline: 0;
    background: transparent;
    color: #111827;
    font-size: 14px;
    font-weight: 700;
}

.student-enrolled__search-box input::placeholder {
    color: #94a3b8;
    font-weight: 600;
}

.student-enrolled__clear {
    display: grid;
    place-items: center;
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: 7px;
    background: #eef6ff;
    color: #0d6efd;
}

.student-enrolled__panel {
    position: relative;
    overflow: hidden;
    padding: 22px;
}

.student-enrolled__panel::before {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: 4px;
    background: linear-gradient(90deg, #0d6efd 0%, #20c997 56%, #7c3aed 100%);
    content: "";
}

.student-enrolled__panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 18px;
}

.student-enrolled__panel-head h2 {
    font-size: 19px;
}

.student-enrolled__table-wrap {
    overflow-x: auto;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
}

.student-enrolled__table {
    width: 100%;
    min-width: 850px;
    border-collapse: collapse;
    background: #ffffff;
}

.student-enrolled__table th,
.student-enrolled__table td {
    padding: 15px 16px;
    border-bottom: 1px solid #eef2f7;
    text-align: left;
    vertical-align: middle;
}

.student-enrolled__table th {
    background: #f8fafc;
    color: #64748b;
    font-size: 12px;
    font-weight: 900;
    text-transform: uppercase;
    white-space: nowrap;
}

.student-enrolled__table td {
    color: #334155;
    font-size: 14px;
}

.student-enrolled__table tbody tr:hover {
    background: #fbfdff;
}

.student-enrolled__table tbody tr:last-child td {
    border-bottom: 0;
}

.student-enrolled__course-title {
    display: grid;
    gap: 4px;
    min-width: 220px;
}

.student-enrolled__course-title strong {
    color: #111827;
    font-size: 14px;
    font-weight: 800;
}

.student-enrolled__progress-cell {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 150px;
}

.student-enrolled__progress {
    flex: 1;
    height: 8px;
    overflow: hidden;
    border-radius: 999px;
    background: #e2e8f0;
}

.student-enrolled__progress span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #0d6efd 0%, #20c997 100%);
}

.student-enrolled__progress-cell small {
    min-width: 36px;
    color: #64748b;
    font-size: 12px;
    font-weight: 800;
}

.student-enrolled__rating {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    color: #f59e0b;
    font-size: 16px;
    white-space: nowrap;
}

.student-enrolled__status {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 28px;
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 800;
    white-space: nowrap;
}

.student-enrolled__status.is-progress,
.student-enrolled__status.is-active,
.student-enrolled__status.is-continue {
    background: #eff6ff;
    color: #2563eb;
}

.student-enrolled__status.is-completed {
    background: #ecfdf5;
    color: #047857;
}

.student-enrolled__status.is-pending {
    background: #fff7ed;
    color: #f97316;
}

.student-enrolled__status.is-inactive {
    background: #fef2f2;
    color: #dc2626;
}

.student-enrolled__status.is-expired {
    background: #fef2f2;
    color: #dc2626;
}

.student-enrolled__continue {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 34px;
    padding: 7px 11px;
    border: 0;
    border-radius: 8px;
    background: #e7f1ff;
    color: #0d6efd;
    cursor: pointer;
    font-size: 12px;
    font-weight: 900;
    transition: all 0.2s ease;
    white-space: nowrap;
}

.student-enrolled__continue:hover {
    background: #dbeafe;
    color: #0b5ed7;
    transform: translateY(-1px);
}

.student-enrolled__continue.is-disabled {
    background: #fef2f2;
    color: #dc2626;
}

.student-enrolled__empty {
    display: grid;
    justify-items: center;
    gap: 8px;
    padding: 34px 16px;
    color: #64748b;
    text-align: center;
}

.student-enrolled__empty i {
    display: grid;
    place-items: center;
    width: 54px;
    height: 54px;
    border-radius: 50%;
    background: #e7f1ff;
    color: #0d6efd;
    font-size: 26px;
}

.student-enrolled__empty strong {
    color: #111827;
    font-size: 16px;
    font-weight: 900;
}

.student-enrolled__empty span {
    font-size: 13px;
    font-weight: 600;
}

.student-enrolled__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-top: 18px;
}

.student-enrolled__footer p {
    margin: 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 700;
}

.student-enrolled__pagination {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 7px;
}

.student-enrolled__pagination a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 36px;
    height: 36px;
    padding: 0 11px;
    border: 1px solid #dbe4ef;
    border-radius: 8px;
    background: #ffffff;
    color: #475569;
    font-size: 13px;
    font-weight: 800;
    line-height: 1;
    text-decoration: none;
    transition: all 0.2s ease;
}

.student-enrolled__pagination a:hover {
    border-color: #0d6efd;
    color: #0d6efd;
}

.student-enrolled__pagination a.is-active {
    border-color: #0d6efd;
    background: #0d6efd;
    color: #ffffff;
}

.student-enrolled__pagination a.is-disabled {
    pointer-events: none;
    opacity: 0.45;
}

.text-end {
    text-align: right !important;
}

@media (max-width: 1199px) {
    .student-enrolled__container {
        grid-template-columns: 210px minmax(0, 1fr);
    }
}

@media (max-width: 991px) {
    .student-enrolled {
        padding: 24px 0 36px;
    }

    .student-enrolled__container,
    .student-enrolled__search-panel {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 767px) {
    .student-enrolled__topbar,
    .student-enrolled__panel-head,
    .student-enrolled__footer {
        flex-direction: column;
        align-items: flex-start;
    }

    .student-enrolled__pagination {
        justify-content: flex-start;
    }
}

@media (max-width: 575px) {
    .student-enrolled__container {
        padding: 0 10px;
    }

    .student-enrolled__topbar,
    .student-enrolled__search-panel,
    .student-enrolled__panel {
        padding: 16px;
    }

    .student-enrolled__back {
        width: 100%;
        justify-content: center;
    }

    .student-enrolled__search-box {
        grid-template-columns: 22px minmax(0, 1fr) auto;
    }
}
</style>
